import axios from "axios";

import { api, apiRequest } from "@/lib/api";

export type RecipeStatus = "draft" | "submitted";

export interface RecipeCapabilities {
  can_edit?: boolean;
  can_submit?: boolean;
  can_delete?: boolean;
  // False when the console user can't open the recipe's food page. Absent on
  // the old backend: treat as true whenever the recipe has a food_item_id.
  can_manage_servings?: boolean;
}

// Shipped blocker shape (brief §2/§6.1): {code, message} plus an optional
// ingredient_id on ingredient-scoped blockers. No `field`.
export interface RecipeBlocker {
  code: string;
  message: string;
  ingredient_id?: string;
}

// Shipped warning shape (brief §6.14): {code, severity, message} only.
export interface RecipeWarning {
  code: string;
  severity?: string;
  message: string;
}

export interface RecipeIngredient {
  id: string;
  food_item_id: number;
  food_name: string;
  food_code?: string | null;
  serving_id: number;
  serving_label: string;
  serving_multiplier: number;
  // Coerced to 0 server-side when the serving has no weight, so it cannot be
  // told apart from a genuine zero. Use requires_whole_servings, not this.
  serving_weight_g: number;
  // Read-only. True when this ingredient can only be used in whole servings —
  // set for a recipe by its own flag, and for a plain food by its serving
  // having no weight. Half a capsule is no more an amount here than in a plan.
  requires_whole_servings: boolean;
  quantity: number;
  retention_factor: number;
  row_order?: number;
  row_weight_g?: number;
  nutrients?: Record<string, number>;
  // This ingredient's own classification, as the food item carries it: the
  // allergens it contains and the diet claims they leave standing. Optional
  // until Columbus fix/recipe-ingredient-diet-tags deploys; the ingredient row
  // shows nothing when absent.
  allergen_tags?: RecipeResolvedTag[];
  diet_flags?: RecipeDietFlag[];
}

export interface RecipeServingRow {
  id: string;
  kind?: string;
  serving_unit_code: number;
  serving_unit_name: string;
  // Absent on the old backend.
  description?: string;
  weight_g: number;
  servings_per_recipe?: number;
  multiplier: number;
  is_default: boolean;
  row_order?: number;
}

// Shipped tag shape (brief §6.5): origin is "ingredient" | "manual"; the
// pointer key is source_food_item_id (omitted for manual tags).
export interface RecipeResolvedTag {
  id: number;
  name: string;
  slug?: string;
  origin?: "manual" | "ingredient";
  locked?: boolean;
  source_food_item_id?: number;
}

// An allergen tag proves a food contains something; a missing tag proves
// nothing. So a diet claim can be broken by a tag but never proven by the
// absence of one: it is either "fail" or "unknown", never a pass.
export type RecipeDietFlagStatus = "fail" | "unknown";

export interface RecipeDietFlag {
  code: string;
  // false = broken; null = unknown. Older backends send true for "no tag found",
  // which is unknown too, never a pass.
  value: boolean | null;
  status?: RecipeDietFlagStatus;
  broken_by_ingredient_id?: number | null;
  // Every ingredient (food_item_id) that breaks the claim.
  broken_by_ingredient_ids?: number[];
  // True when an allergen added by hand on the recipe also breaks it.
  broken_by_manual?: boolean;
}

export interface RecipeLabelRow {
  key: string;
  label: string;
  unit: "kcal" | "kJ" | "g" | "mg";
  per_serve: number | null;
  per_100g: number | null;
  nrv_pct: number | null;
  bold: boolean;
  indent: boolean;
}

export interface RecipeTagInfo {
  texture?: string;
  cuisine_tag_ids?: number[];
  feeding_category_ids?: number[];
  meal_type_ids?: number[];
  condition_tag_ids?: number[];
  allergen_tag_ids?: number[];
  allergen_tag_intent_ids?: number[];
  age_suitability_tag_ids?: number[];
  age_suitability_tag_intent_ids?: number[];
  context_tag_ids?: number[];
}

export interface RecipeSummary {
  id: string;
  food_item_id: number | null;
  food_code: string | null;
  name: string;
  hindi_name?: string;
  category?: string;
  status: RecipeStatus;
  version: number;
  is_homemade?: boolean;
  is_favorite?: boolean;
  servings_per_recipe: number;
  // null only when the recipe has no ingredients at all — FoodItem.energy_kcal
  // can't be null, so every ingredient contributes energy. That makes null an
  // uncosted draft, which can't be published, so it never appears on a
  // published row. Uncosted drafts sort LAST in both directions on
  // ordering=energy_kcal_per_serving (nothing to rank, not the lightest).
  kcal_per_serving: number | null;
  ingredient_count: number;
  updated_at: string;
  available_transitions?: string[];
  // True when a published recipe has staged edits not yet published — drives
  // the "Publish" affordance (brief §1, Columbus #392). Present on list + detail.
  has_unpublished_changes?: boolean;
  // Semantic revision of the live food version (1, 2, 3…); null until first
  // publish, advances only on publish. Rendered as `food_code · v{n}`. NOT the
  // `version` autosave counter.
  food_version_number?: number | null;
  // The version the staged draft will occupy once published (reliable for the
  // recipe flow: one draft + If-Match-gated publish). null when no staged draft.
  draft_target_version_number?: number | null;
  // Shared-draft attribution: recipe drafts are shared across console users, so
  // the staged changes may not be yours. Used to warn before publishing someone
  // else's edit.
  draft_updated_by_id?: string | null;
  draft_updated_by?: string | null;
  draft_updated_at?: string | null;
}

// Full per-nutrient breakdown for the Recipe Nutrient Profile tab (Columbus
// `fix/recipe-full-nutrient-profile`). Additive: the three `nutrients_per_*`
// maps still ship. Unlike those label-keyed maps — which only surface the ~15
// nutrients with a matching catalog field — this carries the COMPLETE nutrient
// set (every amino acid, vitamin, mineral) as ready-to-render rows in native
// units. Rows are pre-sorted by clinical group order; render verbatim.
export interface RecipeNutrientProfileRow {
  nutrient_slug: string;
  nutrient_name: string;
  // Clinical group code: GEN | CARB | LIP | PROT | VIT | MIN. Section headers
  // are derived from this in order of first appearance.
  group: string;
  unit: string;
  // null = no ingredient supplied this nutrient (render "—"); 0 = computed and
  // genuinely zero (render "0"). Native unit — fat sub-rows arrive in mg, so
  // render `amount` + `unit` verbatim with no client-side conversion.
  amount: number | null;
  // Always null on the recipe tab today; the tab hides the %DV column.
  daily_value_percentage: number | null;
}

export interface RecipeNutrientProfile {
  per_serve: RecipeNutrientProfileRow[];
  per_100g: RecipeNutrientProfileRow[];
  per_recipe: RecipeNutrientProfileRow[];
}

export interface RecipeDetail extends RecipeSummary {
  food_type?: string;
  full_name?: string;
  notes?: string;
  source?: string;
  source_id?: string;
  source_url?: string;
  source_confidence?: string;
  brand?: string;
  serving_mode?: "serving" | "weight";
  cooked_weight_g?: number | null;
  total_weight_g?: number | null;
  submitted_at?: string | null;
  ingredients: RecipeIngredient[];
  servings: {
    servings_per_recipe: number;
    core_rows?: RecipeServingRow[];
    serving_rows: RecipeServingRow[];
    // Servings on the recipe's food. The recipe and its food share one food
    // code and one servings list, so the recipe shows these read-only; they are
    // never sent back as recipe rows. The new backend returns every live
    // non-built-in serving here and an empty serving_rows once published; the
    // old one still puts recipe-authored units in serving_rows.
    food_rows?: RecipeServingRow[];
  };
  tag_info: RecipeTagInfo;
  // All three bases are always sent and carry an identical key set, so the
  // nutrient-basis switcher can flip between them without dropping a row.
  // Values are rounded server-side (per_100g to 2dp).
  //
  // null and 0 mean different things and must render differently: null is "no
  // ingredient supplied this nutrient" (render "—"), 0 is "computed, and it is
  // zero" (render "0"). A whole basis comes back all-null when it isn't
  // computable — per_100g when total weight is 0, per_serve when servings is 0.
  nutrients_per_recipe: Record<string, number | null>;
  nutrients_per_serve: Record<string, number | null>;
  nutrients_per_100g: Record<string, number | null>;
  // How many ingredients supplied each nutrient, counted per ingredient (a food
  // carrying two aliases of the same nutrient counts once). Same key set as the
  // three bases above. NOT the inverse of null: coverage 0 does imply null
  // everywhere, but a non-zero coverage can still be null on a basis that isn't
  // computable. Compare against ingredients.length to spot partial data — a
  // real figure that understates the dish.
  nutrients_coverage: Record<string, number>;
  // Full nutrient breakdown per basis (additive; see RecipeNutrientProfile).
  // Optional until Columbus `fix/recipe-full-nutrient-profile` deploys — the
  // Nutrient Profile tab falls back to the label-keyed maps above when absent.
  nutrient_profile?: RecipeNutrientProfile;
  label_rows: RecipeLabelRow[];
  diet_flags: RecipeDietFlag[];
  derived_tags: {
    allergen_tags: RecipeResolvedTag[];
    age_suitability_tags: RecipeResolvedTag[];
    // The age tags a clinician may narrow to: only bands every ingredient
    // allows. Optional until Columbus fix/recipe-ingredient-diet-tags deploys;
    // the picker falls back to the full catalog when absent.
    age_suitability_options?: RecipeResolvedTag[];
    // Context tags as resolved: the clinician's picks plus "Fasting Safe",
    // which the recipe carries only when every ingredient does (locked).
    context_tags?: RecipeResolvedTag[];
  };
  warnings: RecipeWarning[];
  blockers: RecipeBlocker[];
  capabilities: RecipeCapabilities;
}

export interface RecipeListParams {
  search?: string;
  status?: RecipeStatus;
  category?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface RecipeListResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: RecipeSummary[];
  status_counts?: Partial<Record<RecipeStatus, number>>;
}

export interface RecipeIngredientInput {
  food_item_id: number;
  serving_id: number;
  quantity: string;
  retention_factor?: string;
  row_order?: number;
}

// `multiplier` is derived from weight_g and ignored on write once columbus#404
// merges — but that PR is not on prod yet, and the backend still requires the
// field. Kept optional in the type, still sent by the recipe form.
export interface RecipeServingInput {
  serving_unit_code: number;
  // Display name, built like the food page's. The current backend ignores it.
  description?: string;
  weight_g: string;
  multiplier?: string;
  is_default?: boolean;
  row_order?: number;
}

export interface RecipeWritePayload {
  idempotency_key?: string;
  name?: string;
  hindi_name?: string;
  full_name?: string;
  source?: string;
  source_id?: string;
  source_url?: string;
  source_confidence?: string;
  brand?: string;
  is_homemade?: boolean;
  is_favorite?: boolean;
  notes?: string;
  serving_mode?: "serving" | "weight";
  servings_per_recipe?: number;
  cooked_weight_g?: string | null;
  texture?: string;
  ingredients?: RecipeIngredientInput[];
  manual_servings?: RecipeServingInput[];
  serving_rows?: RecipeServingInput[];
  tag_info?: RecipeTagInfo;
}

export interface RecipeConflictError extends Error {
  status: 409;
  currentRecipeVersion?: number;
  currentRecipe?: RecipeDetail;
}

export interface RecipeBlockerError extends Error {
  status: 400;
  blockers: RecipeBlocker[];
}

const isRecipeDetail = (value: unknown): value is RecipeDetail => {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    "id" in value &&
    "version" in value &&
    "ingredients" in value
  );
};

const buildQueryParams = (params?: RecipeListParams) => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.status) queryParams.append("status", params.status);
  if (params?.category && params.category !== "all") {
    queryParams.append("category", params.category);
  }
  if (params?.ordering) queryParams.append("ordering", params.ordering);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.page_size) {
    queryParams.append("page_size", params.page_size.toString());
  }
  return queryParams.toString();
};

const createRecipeConflictError = (data: unknown): RecipeConflictError => {
  const record =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {};
  const error = new Error("Recipe version conflict") as RecipeConflictError;
  error.status = 409;
  error.currentRecipeVersion =
    typeof record.current_recipe_version === "number"
      ? record.current_recipe_version
      : undefined;
  error.currentRecipe = isRecipeDetail(data) ? data : undefined;
  return error;
};

const createRecipeBlockerError = (data: unknown): RecipeBlockerError => {
  const record =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {};
  const error = new Error("Recipe has submit blockers") as RecipeBlockerError;
  error.status = 400;
  error.blockers = Array.isArray(record.blockers)
    ? (record.blockers as RecipeBlocker[])
    : [];
  return error;
};

export const isRecipeConflictError = (
  error: unknown
): error is RecipeConflictError =>
  Boolean(error) &&
  typeof error === "object" &&
  (error as Partial<RecipeConflictError>).status === 409;

export const isRecipeBlockerError = (
  error: unknown
): error is RecipeBlockerError =>
  Boolean(error) &&
  typeof error === "object" &&
  (error as Partial<RecipeBlockerError>).status === 400 &&
  Array.isArray((error as Partial<RecipeBlockerError>).blockers);

const recipeRequest = async <T>(requestFn: () => Promise<{ data: T }>) => {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      throw createRecipeConflictError(error.response.data);
    }
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      const data = error.response.data;
      if (
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        Array.isArray((data as Record<string, unknown>).blockers)
      ) {
        throw createRecipeBlockerError(data);
      }
    }
    throw error;
  }
};

export const recipeAPI = {
  list: async (params?: RecipeListParams): Promise<RecipeListResponse> => {
    const queryString = buildQueryParams(params);
    return apiRequest(() =>
      api.get(`/nutrition/recipes/${queryString ? `?${queryString}` : ""}`)
    );
  },

  get: async (id: string): Promise<RecipeDetail> => {
    return apiRequest(() => api.get(`/nutrition/recipes/${id}/`));
  },

  create: async (data: RecipeWritePayload): Promise<RecipeDetail> => {
    // idempotency_key is required on create (brief §4). Guarantee one so a
    // caller that forgets it can't trigger a 400.
    const payload: RecipeWritePayload = {
      ...data,
      idempotency_key: data.idempotency_key ?? crypto.randomUUID(),
    };
    return apiRequest(() => api.post("/nutrition/recipes/", payload));
  },

  update: async (
    id: string,
    version: number,
    data: RecipeWritePayload
  ): Promise<RecipeDetail> => {
    return recipeRequest(() =>
      api.patch(`/nutrition/recipes/${id}/`, data, {
        // Backend compares the raw header string to str(version) (brief §5),
        // so send the bare integer — NOT a quoted ETag ("7"), which never
        // matches and 409s on every save.
        headers: { "If-Match": String(version) },
      })
    );
  },

  submit: async (
    id: string,
    version: number,
    idempotencyKey: string
  ): Promise<RecipeDetail> => {
    return recipeRequest(() =>
      api.post(
        `/nutrition/recipes/${id}/submit/`,
        { idempotency_key: idempotencyKey },
        { headers: { "If-Match": String(version) } }
      )
    );
  },

  // Throw away the staged draft and revert to the last-published version
  // (clears has_unpublished_changes). If-Match required; no body.
  discard: async (id: string, version: number): Promise<RecipeDetail> => {
    return recipeRequest(() =>
      api.post(`/nutrition/recipes/${id}/discard/`, undefined, {
        headers: { "If-Match": String(version) },
      })
    );
  },

  delete: async (id: string): Promise<void> => {
    return apiRequest(() => api.delete(`/nutrition/recipes/${id}/`));
  },
};
